//for use in inventory page and clinic equipment page

import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, notification, Tabs } from "antd";
import {
  CheckOutlined,
  FileTextOutlined,
  SendOutlined,
} from "@ant-design/icons";
import axiosInstance from "utilities/axios";
import useRoleCheck from "utilities/hooks/roleCheck";

import HistoryContent from "./components/HistoryContent";
import DocumentationContent from "./components/DocumentationContent";
import RequestContent from "./components/RequestContent";

import ApprovalPopup from "./ApprovalPopup";
import ShippingPopup from "./ShippingPopup";
import DocumentationPopup from "./DocumentationPopup";

function ItemDisplay({ id, open, onClose }) {
  const { hasPermission } = useRoleCheck();

  const [requestContent, setRequestContent] = useState(null);
  const [approvalVisible, setApprovalVisible] = useState(false);
  const [shippingvisible, setShippingVisible] = useState(false);
  const [documentationVisible, setDocumentationVisible] = useState(false);

  const onChange = useCallback(() => {
    if (!id) {
      setRequestContent(null);
      return;
    }
    axiosInstance
      .get(`/equipment/requests/${id}`)
      .then((response) => {
        setRequestContent(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  }, [id]);

  useEffect(() => {
    onChange();
  }, [onChange]);

  const uploadFile = (form) => {
    form.validateFields().then((values) => {
      var formData = new FormData();
      if (values.file?.file) {
        formData.append("file", values.file.file);
      }

      if (values.type) {
        formData.append("type", values.type);
      }
      if (values.number) {
        formData.append("number", values.number);
      }

      if (values.date) {
        formData.append("date", values.date.format("YYYY-MM-DD"));
      }

      axiosInstance
        .post(`/equipment/requests/${id}/documents`, formData)
        .then((response) => {
          onChange();
          form.resetFields();
          setDocumentationVisible(false);
          notification.success({
            message: "Success",
            description: "Document uploaded successfully",
          });
        })
        .catch((error) => {
          notification.error({
            message: "Error",
            description: error.message,
          });
        });
    });
  };
  const deleteDoc = (doc_id) => {
    axiosInstance
      .delete(`/equipment/requests/${id}/documents/${doc_id}`)
      .then((response) => {
        onChange();

        notification.success({
          message: "Success",
          description: "Document deleted successfully",
        });
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  };

  if (!requestContent) {
    return null;
  }

  //if the request is "Submitted" then there will be a button for the approval popup, if it is "Approved" then there will be a link to the shipped popup. If it is not "Complete" there will be a button for the documentation popup
  //also check user roles, only Audiology.Admin, should be able to approve the request or if the user role is in Sales.RM and the ClinicNo is in their available clinics and the request type is Transfer
  const footerContent = [];
  if (
    requestContent.currentStatus === "Submitted" &&
    !(hasPermission(["Audiology.Admin"]) &&
      ["Digital Signature Pad", "Barcode Scanner"].includes(requestContent.equipment.category)) &&
    // scenario 1 to allow RM or DSD to approve if current approval type is RM
    ((hasPermission(
      ["Audiology.Admin"],
      ["Sales.RM", "Sales.Exec"],
      requestContent?.clinicNo,
      false
    ) &&
      requestContent.currentApprovalType === "Regional Manager") ||
      // scenario 3 to allow other RM to approve if current approval type is Other RM
      (hasPermission(
        ["Audiology.Admin"],
        ["Sales.RM", "Sales.Exec"],
        requestContent?.sourceClinicNo,
        false
      ) &&
        requestContent.currentApprovalType === "Other Regional Manager") ||
      // scenario 2 to only allow audiology admin to approve
      (hasPermission(
        ["Audiology.Admin"],
        [],
        requestContent?.clinicNo,
        false
      ) &&
        requestContent.currentApprovalType === "Admin"))
  ) {
    footerContent.push(
      <Button
        key="approve"
        icon={<CheckOutlined />}
        onClick={() => setApprovalVisible(true)}
      >
        Approve/Reject Request
      </Button>
    );
  }

  if (
    requestContent.currentStatus === "Approved" &&
    hasPermission(
      ["Audiology"],
      ["Sales.RM", "Sales.Staff", "Sales.Exec"],
      requestContent.clinicNo,
      false
    )
  ) {
    footerContent.push(
      <Button
        key="ship"
        icon={<SendOutlined />}
        onClick={() => setShippingVisible(true)}
      >
        Set as Shipped
      </Button>
    );
  }

  if (
    requestContent.currentStatus !== "Completed" &&
    hasPermission(
      ["Audiology.Admin"],
      ["Sales.RM", "Sales.Staff", "Sales.Exec"],
      requestContent.clinicNo
    )
  ) {
    footerContent.push(
      <Button
        key="documentation"
        icon={<FileTextOutlined />}
        onClick={() => setDocumentationVisible(true)}
      >
        Add Documentation
      </Button>
    );
  }
  return (
    <>
      <Modal
        title="Request Details"
        open={open}
        onCancel={onClose}
        footer={footerContent}
        width={800}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Details" key="1">
            <RequestContent
              clinicNo={requestContent.clinicNo}
              equipment={requestContent.equipment}
              requestType={requestContent.requestType}
              currentStatus={requestContent.currentStatus}
              requestDate={requestContent.requestDate}
              sourceClinicNo={requestContent.sourceClinicNo}
              estimatedCost={requestContent.estimatedCost}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="History" key="2">
            <HistoryContent history={requestContent.history} />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Documentation" key="3">
            <DocumentationContent
              documentation={requestContent.documentation}
              editable={requestContent.currentStatus !== "Completed"}
              deleteDoc={deleteDoc}
            />
          </Tabs.TabPane>
        </Tabs>
      </Modal>
      <ApprovalPopup
        requestId={id}
        isOpen={approvalVisible}
        onClose={() => setApprovalVisible(false)}
        onChange={onChange}
      />
      <ShippingPopup
        itemId={id}
        isOpen={shippingvisible}
        onClose={() => setShippingVisible(false)}
        onSubmit={onChange}
      />
      <DocumentationPopup
        isOpen={documentationVisible}
        onClose={() => setDocumentationVisible(false)}
        uploadFiles={uploadFile}
      />
    </>
  );
}

export default ItemDisplay;
